import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronLeft, ChevronRight, Download, Printer } from "lucide-react";
import { api, downloadCsv, formatApiError, gbp } from "@/lib/api";

function londonMondayIso(offsetWeeks = 0) {
  const london = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/London" }));
  const day = (london.getDay() + 6) % 7;
  london.setDate(london.getDate() - day + offsetWeeks * 7);
  const pad = (n) => String(n).padStart(2, "0");
  return `${london.getFullYear()}-${pad(london.getMonth() + 1)}-${pad(london.getDate())}`;
}

function weekLabel(iso) {
  const d = new Date(`${iso}T12:00:00`);
  return `Week commencing ${d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}`;
}

function dayLabel(iso) {
  return new Date(`${iso}T12:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
}

const STATE_COLOURS = {
  confirmed: "text-green-400", in_preparation: "text-blue-300", ready_for_collection: "text-gold",
  out_for_delivery: "text-gold", completed: "text-cream-muted",
  awaiting_payment: "text-amber-300", payment_processing: "text-amber-300",
  requires_attention: "text-red-300", cancelled: "text-red-400",
};

export default function AdminProduction() {
  const [week, setWeek] = useState(londonMondayIso(0));
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    setData(null);
    api.get("/admin/production", { params: { week } }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/exports/audit").then((r) => setAudit(r.data)).catch(() => {});
  }, [week]);
  useEffect(() => { load(); }, [load]);

  const shift = (weeks) => {
    const d = new Date(`${week}T12:00:00`);
    d.setDate(d.getDate() + weeks * 7);
    const pad = (n) => String(n).padStart(2, "0");
    setWeek(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
  };

  const doExport = (kind, path) => {
    setBusy(kind);
    downloadCsv(path, { week })
      .then(() => { toast.success("Export downloaded and recorded in the audit history."); load(); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(""));
  };

  return (
    <div data-testid="admin-production-page">
      <div className="no-print flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-2xl font-bold text-cream-text">Weekly production</h1>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => shift(-1)} className="rounded-full border border-choco-line p-2 text-cream-muted hover:text-gold" aria-label="Previous week" data-testid="production-prev-week"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => setWeek(londonMondayIso(0))} className="rounded-full border border-choco-line px-4 py-2 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid="production-this-week">This week</button>
          <button onClick={() => shift(1)} className="rounded-full border border-choco-line p-2 text-cream-muted hover:text-gold" aria-label="Next week" data-testid="production-next-week"><ChevronRight className="h-4 w-4" /></button>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-full border border-choco-line px-4 py-2 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid="production-print-button"><Printer className="h-3.5 w-3.5" aria-hidden="true" /> Print kitchen list</button>
        </div>
      </div>
      <p className="no-print mt-1 text-xs text-cream-muted">Production quantities are never guessed — anything without a configured multiplier is flagged as unknown.</p>
      <h2 className="mt-4 font-serif text-lg font-semibold text-gold" data-testid="production-week-label">{weekLabel(week)}</h2>

      <div className="no-print mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="production-summary">
        <div className="rounded-2xl border border-choco-line bg-choco-light p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Fulfilling orders</p>
          <p className="mt-1 font-serif text-2xl font-bold text-cream-text" data-testid="production-fulfilling-count">{data ? data.fulfilling_count : "…"}</p>
        </div>
        <div className="rounded-2xl border border-choco-line bg-choco-light p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Outstanding on collection</p>
          <p className="mt-1 font-serif text-2xl font-bold text-amber-300" data-testid="production-outstanding">{data ? gbp(data.outstanding_collection_pence) : "…"}</p>
        </div>
        <div className="rounded-2xl border border-choco-line bg-choco-light p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Separated orders</p>
          <p className="mt-1 font-serif text-2xl font-bold text-cream-text" data-testid="production-separated-count">{data ? data.separated_count : "…"}</p>
        </div>
        <div className="rounded-2xl border border-choco-line bg-choco-light p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Exports</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <button disabled={!!busy} onClick={() => doExport("production", "/admin/exports/production.csv")} className="inline-flex items-center gap-1 rounded-full bg-gold px-3 py-1 text-[11px] font-bold text-choco disabled:opacity-50" data-testid="export-production-button"><Download className="h-3 w-3" aria-hidden="true" /> Production</button>
            <button disabled={!!busy} onClick={() => doExport("fulfilment", "/admin/exports/fulfilment.csv")} className="inline-flex items-center gap-1 rounded-full bg-gold px-3 py-1 text-[11px] font-bold text-choco disabled:opacity-50" data-testid="export-fulfilment-button"><Download className="h-3 w-3" aria-hidden="true" /> Fulfilment</button>
            <button disabled={!!busy} onClick={() => doExport("balances", "/admin/exports/balances.csv")} className="inline-flex items-center gap-1 rounded-full bg-gold px-3 py-1 text-[11px] font-bold text-choco disabled:opacity-50" data-testid="export-balances-button"><Download className="h-3 w-3" aria-hidden="true" /> Balances</button>
          </div>
        </div>
      </div>

      <div className="print-sheet mt-6 space-y-6" data-testid="production-weeks">
        {data === null && <p className="text-sm text-cream-muted">Loading…</p>}
        {data?.weeks.length === 0 && (
          <p className="rounded-3xl border border-choco-line bg-choco-light p-8 text-center text-sm text-cream-muted" data-testid="production-empty">No fulfilling orders this week.</p>
        )}
        {data?.weeks.map((day) => (
          <section key={day.date} className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid={`production-day-${day.date}`}>
            <h3 className="font-serif text-lg font-semibold text-cream-text">{dayLabel(day.date)}</h3>
            <p className="text-xs text-cream-muted">{day.orders.length} order{day.orders.length === 1 ? "" : "s"}</p>
            <div className="mt-4 space-y-5">
              {day.slots.map((slot) => (
                <div key={slot.slot} className="rounded-2xl border border-choco-line p-4" data-testid={`production-slot-${day.date}-${slot.slot}`}>
                  <p className="text-xs font-bold uppercase tracking-widest text-gold">{slot.slot}</p>
                  <table className="mt-3 w-full text-left text-sm">
                    <thead>
                      <tr className="text-[11px] uppercase tracking-widest text-cream-muted">
                        <th className="pb-2 pr-3 font-semibold">Product</th>
                        <th className="pb-2 pr-3 font-semibold">Customer units</th>
                        <th className="pb-2 pr-3 font-semibold">Production</th>
                        <th className="pb-2 font-semibold">Allergens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slot.products.map((p, i) => (
                        <tr key={i} className="border-t border-choco-line align-top">
                          <td className="py-2 pr-3 text-cream-text">
                            {p.name}
                            {Object.keys(p.variation || {}).length > 0 && (
                              <span className="block text-xs text-cream-muted">{Object.entries(p.variation).map(([k, v]) => `${k}: ${v}`).join(" · ")}</span>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-cream-text" data-testid={`units-${day.date}-${p.name}`}>{p.customer_units}</td>
                          <td className="py-2 pr-3">
                            {p.production?.quantity != null ? (
                              <span className="font-bold text-cream-text">{p.production.quantity} {p.production.unit}</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 px-2.5 py-0.5 text-[11px] font-bold text-amber-300" data-testid={`unknown-multiplier-${day.date}-${p.name}`}>
                                <AlertTriangle className="h-3 w-3" aria-hidden="true" /> Unknown — multiplier not set
                              </span>
                            )}
                          </td>
                          <td className="py-2 text-xs capitalize text-cream-muted">{p.allergens?.length ? p.allergens.join(", ") : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <ul className="mt-3 space-y-1 border-t border-choco-line pt-3 text-xs text-cream-muted">
                    {slot.orders.map((o) => (
                      <li key={o.order_id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <Link to={`/admin/orders/${o.order_id}`} className="font-bold text-gold hover:underline" data-testid={`production-order-${o.order_number}`}>{o.order_number}</Link>
                        <Link to={`/admin/production/packing/${o.order_id}`} className="no-print rounded-full border border-choco-line px-2 py-0.5 text-[10px] font-bold text-cream-muted hover:border-gold hover:text-gold" data-testid={`packing-link-${o.order_number}`}>Packing sheet</Link>
                        <span>{o.customer}</span>
                        <span className={`font-bold capitalize ${STATE_COLOURS[o.order_status] || ""}`}>{o.order_status.replace(/_/g, " ")}</span>
                        <span className="capitalize">{o.payment_status.replace(/_/g, " ")}</span>
                        {o.amount_due_pence > 0 && <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold text-amber-300">Collect {gbp(o.amount_due_pence)}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {data && data.separated.length > 0 && (
        <section className="no-print mt-8 rounded-3xl border border-amber-500/30 bg-choco-light p-6" data-testid="production-separated">
          <h3 className="font-serif text-lg font-semibold text-amber-300">Not in production totals</h3>
          <p className="mt-1 text-xs text-cream-muted">Awaiting payment, processing, requires-attention and cancelled orders are listed separately and never counted above.</p>
          <ul className="mt-4 space-y-2 text-sm">
            {data.separated.map((o) => (
              <li key={o.order_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-cream-muted">
                <Link to={`/admin/orders/${o.order_id}`} className="font-bold text-gold hover:underline" data-testid={`separated-order-${o.order_number}`}>{o.order_number}</Link>
                <span className={`font-bold capitalize ${STATE_COLOURS[o.order_status] || ""}`}>{o.order_status.replace(/_/g, " ")}</span>
                <span className="capitalize">{o.payment_status.replace(/_/g, " ")}</span>
                <span>{gbp(o.total_pence)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="no-print mt-8 rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="export-audit-section">
        <h3 className="font-serif text-lg font-semibold text-cream-text">Export history</h3>
        <p className="mt-1 text-xs text-cream-muted">Every customer-data export is recorded here.</p>
        {audit.length === 0 ? (
          <p className="mt-3 text-sm text-cream-muted" data-testid="export-audit-empty">No exports recorded yet.</p>
        ) : (
          <ul className="mt-3 space-y-1 text-xs text-cream-muted">
            {audit.map((a) => (
              <li key={a.id} data-testid={`export-audit-${a.id}`}>
                <span className="font-bold capitalize text-cream-text">{a.kind}</span> — {a.row_count} rows · {a.created_at_london}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
